import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import {
  Bar, BarChart, CartesianGrid, Line, LineChart, ReferenceArea, ReferenceLine,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { NOT_TRACKED, SERIES_COLOR, STAGE_COLOR, bandDotColor } from "./sleepFields";

const hoursTick = (v) => `${v}h`;
const toHours = (m) => (m === null || m === undefined ? null : Number((m / 60).toFixed(2)));

export const MetricChips = ({ metrics, active, onSelect }) => (
  <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="sleep-metric-chips">
    {metrics.map((m) => (
      <button
        key={m.key}
        type="button"
        onClick={() => onSelect(m.key)}
        className={`whitespace-nowrap rounded-full border px-4 py-2 text-sm transition-colors ${
          active === m.key
            ? "bg-primary text-primary-foreground border-primary"
            : "bg-background border-border text-muted-foreground hover:bg-muted"
        }`}
        data-testid={`sleep-metric-${m.key}`}
      >
        {active === m.key ? "✓ " : ""}{m.label}
      </button>
    ))}
  </div>
);

export const MetricHeadline = ({ metric }) => (
  <div data-testid="sleep-metric-headline">
    <div className="flex flex-wrap items-baseline gap-2">
      <span className="font-editorial text-5xl sm:text-6xl leading-none">
        {metric.headline ?? "—"}
      </span>
      <span className="text-base text-muted-foreground">{metric.headline_suffix}</span>
    </div>
    <p className="text-sm text-muted-foreground mt-2 max-w-2xl" data-testid="sleep-metric-note">
      {metric.note}
    </p>
  </div>
);

export const MetricLegend = ({ legend }) => (
  <div className="flex flex-wrap gap-4 mt-3" data-testid="sleep-metric-legend">
    {(legend || []).map((l) => (
      <span key={l.label} className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
        <span
          className="w-2.5 h-2.5 rounded-full"
          style={{
            background: SERIES_COLOR[l.color_key] || STAGE_COLOR[l.color_key] || "#999",
            opacity: l.color_key === "band" ? 0.4 : 1,
          }}
        />
        {l.label}
      </span>
    ))}
  </div>
);

/** Bed-to-wake pills on a single 18.00 → 18.00 axis. */
const ScheduleChart = ({ points, target }) => {
  const offsets = points.flatMap((p) => [p.bed_offset, p.wake_offset]).filter((v) => v !== null && v !== undefined);
  if (!offsets.length) return <p className="text-xs text-muted-foreground py-10 text-center">{NOT_TRACKED}</p>;
  const all = target ? [...offsets, target.bed_offset, target.wake_offset] : offsets;
  const lo = Math.max(0, Math.min(...all) - 45);
  const hi = Math.min(1440, Math.max(...all) + 45);
  const span = Math.max(1, hi - lo);
  const clock = (offset) => {
    const total = Math.round(offset + 1080) % 1440;
    return `${String(Math.floor(total / 60)).padStart(2, "0")}.${String(total % 60).padStart(2, "0")}`;
  };

  return (
    <div className="flex gap-3" data-testid="sleep-schedule-chart">
      <div className="flex flex-col justify-between text-[10px] text-muted-foreground py-1 w-11 text-right">
        {[0, 0.33, 0.66, 1].map((f) => <span key={f}>{clock(lo + span * f)}</span>)}
      </div>
      <div className="relative flex-1 flex items-stretch gap-2 h-[220px]">
        {target && (
          <span
            className="absolute inset-x-0 rounded-lg pointer-events-none"
            style={{
              top: `${((target.bed_offset - lo) / span) * 100}%`,
              height: `${Math.max(2, ((target.wake_offset - target.bed_offset) / span) * 100)}%`,
              background: `${SERIES_COLOR.band}22`,
              border: `1px dashed ${SERIES_COLOR.band}`,
            }}
            data-testid="sleep-schedule-target"
          />
        )}
        {points.map((p) => {
          const hasData = p.bed_offset !== null && p.wake_offset !== null
            && p.bed_offset !== undefined && p.wake_offset !== undefined;
          const top = hasData ? ((p.bed_offset - lo) / span) * 100 : 0;
          const height = hasData ? ((p.wake_offset - p.bed_offset) / span) * 100 : 0;
          return (
            <div key={p.key} className="flex-1 flex flex-col items-center min-w-[14px]">
              <div className="relative flex-1 w-full">
                {hasData && (
                  <span
                    className="absolute left-1/2 -translate-x-1/2 w-[62%] rounded-full"
                    style={{
                      top: `${top}%`,
                      height: `${Math.max(3, height)}%`,
                      background: SERIES_COLOR.main,
                    }}
                    title={`${clock(p.bed_offset)}–${clock(p.wake_offset)}`}
                  />
                )}
              </div>
              <span className="text-[10px] text-muted-foreground mt-1.5">{p.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const MetricChart = ({ metric, points }) => {
  if (!metric.tracked) {
    return (
      <p className="text-xs text-muted-foreground py-12 text-center" data-testid="sleep-metric-untracked">
        {NOT_TRACKED}
      </p>
    );
  }
  if (metric.chart === "schedule") return <ScheduleChart points={points} target={metric.target} />;

  const data = points.map((p) => ({
    label: p.label,
    total: toHours(p.total_minutes),
    main: toHours(p.main_minutes),
    score: p.score,
    band: p.score_band,
    efficiency: p.efficiency_percent,
    deep: p.stages?.deep ?? null,
    rem: p.stages?.rem ?? null,
    light: p.stages?.light ?? null,
    awake: p.stages?.awake ?? null,
  }));

  const axis = { tick: { fontSize: 10 }, tickLine: false, axisLine: false };

  if (metric.chart === "stages") {
    return (
      <div className="h-[240px]" data-testid="sleep-stages-chart">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 10, right: 12, bottom: 0, left: -20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e8e8e3" vertical={false} />
            <XAxis dataKey="label" {...axis} />
            <YAxis {...axis} tickFormatter={(v) => `${Math.round(v / 60)}h`} />
            <Tooltip contentStyle={{ fontSize: 11, borderRadius: 10 }} formatter={(v, n) => [`${v} min`, n]} />
            {["deep", "light", "rem", "awake"].map((k) => (
              <Bar key={k} dataKey={k} stackId="s" fill={STAGE_COLOR[k]} isAnimationActive={false} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  return (
    <div className="h-[240px]" data-testid={`sleep-${metric.chart}-chart`}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 10, right: 12, bottom: 0, left: -20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e8e8e3" vertical={false} />
          <XAxis dataKey="label" {...axis} />

          {metric.chart === "score" && <YAxis domain={[0, 100]} {...axis} />}
          {metric.chart === "efficiency" && <YAxis domain={[0, 100]} {...axis} tickFormatter={(v) => `${v}%`} />}
          {(metric.chart === "duration" || metric.chart === "main") && (
            <YAxis domain={[0, "auto"]} {...axis} tickFormatter={hoursTick} />
          )}
          <Tooltip contentStyle={{ fontSize: 11, borderRadius: 10 }} />

          {metric.chart === "efficiency" && metric.target && (
            <ReferenceArea
              y1={metric.target.low} y2={metric.target.high}
              fill={SERIES_COLOR.band} fillOpacity={0.18} stroke="none"
            />
          )}
          {(metric.chart === "duration" || metric.chart === "main") && metric.goal_minutes && (
            <ReferenceLine
              y={Number((metric.goal_minutes / 60).toFixed(2))}
              stroke={SERIES_COLOR.band} strokeDasharray="4 4"
              label={{ value: "Goal", position: "insideTopRight", fontSize: 10, fill: SERIES_COLOR.band }}
            />
          )}
          {metric.chart === "score" && (metric.bands || []).map((b) => (
            <ReferenceLine
              key={b.value} y={b.value} stroke={bandDotColor(b.label)}
              strokeDasharray="2 4" strokeOpacity={0.6}
            />
          ))}

          {metric.chart === "duration" && (
            <Line type="monotone" dataKey="total" name="Total sleep" stroke={SERIES_COLOR.total}
              strokeWidth={2} dot={{ r: 3 }} isAnimationActive={false} connectNulls />
          )}
          {(metric.chart === "duration" || metric.chart === "main") && (
            <Line type="monotone" dataKey="main" name="Main sleep" stroke={SERIES_COLOR.main}
              strokeWidth={2} dot={{ r: 3 }} isAnimationActive={false} connectNulls />
          )}
          {metric.chart === "score" && (
            <Line
              type="monotone" dataKey="score" name="Sleep score" stroke={SERIES_COLOR.fair}
              strokeWidth={2} isAnimationActive={false} connectNulls
              dot={({ cx, cy, payload }) => (
                cy === null || cy === undefined ? null : (
                  <circle key={payload.label} cx={cx} cy={cy} r={4}
                    fill={bandDotColor(payload.band)} stroke="#fff" strokeWidth={1} />
                )
              )}
            />
          )}
          {metric.chart === "efficiency" && (
            <Line type="monotone" dataKey="efficiency" name="Efficiency" stroke={SERIES_COLOR.good}
              strokeWidth={2} dot={{ r: 3, fill: SERIES_COLOR.good }} isAnimationActive={false} connectNulls />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export const NightList = ({ rows, metricKey, label, onOpenNight, dayHeading }) => (
  <Card className="rounded-2xl" data-testid="sleep-night-list">
    <CardContent className="p-2">
      <p className="px-3 pt-2 pb-1 font-editorial text-xl">{label}</p>
      {rows.length === 0 ? (
        <p className="px-3 py-6 text-sm text-muted-foreground text-center">No nights in this period.</p>
      ) : rows.map((r) => {
        const cell = r[metricKey] || {};
        return (
          <button
            key={r.date}
            type="button"
            onClick={() => onOpenNight(r.date)}
            className="w-full flex items-center justify-between gap-4 px-3 py-3 rounded-xl hover:bg-muted/60 transition-colors text-left"
            data-testid={`sleep-night-row-${r.date}`}
          >
            <span className="text-sm">{dayHeading(r.date)}</span>
            <span className="text-right">
              <span className="block font-editorial text-2xl leading-none">{cell.value || "—"}</span>
              {cell.sub && <span className="block text-[11px] text-muted-foreground mt-1">{cell.sub}</span>}
            </span>
          </button>
        );
      })}
    </CardContent>
  </Card>
);
